import {
  Box,
  Button,
  Checkbox,
  Flex,
  Group,
  LoadingOverlay,
  Text,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconCheck } from "@tabler/icons-react";
import React, { useEffect, useState } from "react";
import { NotificationExtension } from "../../_base/extension/NotificationExtension";
import {
  isNullOrUndefined,
  isNullOrUndefinedArry,
} from "../../_base/extension/StringExtension";
import Repository from "../../_base/helper/HttpHelper";
import { API_ROUTE } from "../../const/apiRoute";
import { MessageResponse } from "../../model/MessageResponse";
import { CategoryTree } from "../../model/TblCategoryCommand";
import { TblCategoryAttributeModel } from "../../model/TblCategoryAttributeModel";
import ButtonClose from "../../common/ButtonClose/ButtonClose";
import AttributeFormStore from "../../store/AttributeFormStore";

interface AttributeCategoryManagerProps {
  attributeId?: number;
  onSaveFinal?: () => void;
  store: AttributeFormStore;
}

const AttributeCategoryManagerNew = ({
  attributeId,
  onSaveFinal,
  store,
}: AttributeCategoryManagerProps) => {
  const [dataCategory, setDataCategory] = useState<CategoryTree[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<
    TblCategoryAttributeModel[]
  >([]);
  const [visible, { open, close }] = useDisclosure(false);
  const [showAllCategories, setShowAllCategories] = useState(false);

  const repository = new Repository(process.env.REACT_APP_Demo_APP_API_URL);

  // Load dữ liệu từ store
  useEffect(() => {
    const savedCategories = store.getSelectedCategories();
    console.log(
      "AttributeCategoryManagerNew mounted, saved categories from store:",
      savedCategories
    );
    if (savedCategories.length > 0) {
      setSelectedCategories(savedCategories);
    }
    // Load category tree
    loadCategoryTree();
  }, []);

  // ✅ Tự động lưu vào store khi selectedCategories thay đổi (chỉ khi có sự thay đổi thực sự)
  useEffect(() => {
    const currentStoreCategories = store.getSelectedCategories();

    // Chỉ update store nếu data thực sự khác
    if (
      JSON.stringify(selectedCategories) !==
      JSON.stringify(currentStoreCategories)
    ) {
      console.log("Saving categories to store:", selectedCategories);
      store.setSelectedCategories(selectedCategories);
    }
  }, [selectedCategories, store]);

  const loadCategoryTree = async () => {
    open();
    try {
      let urlSearch =
        API_ROUTE.GET_CATEGORY_TREE +
        `?CategoryType=category&IsTradeIn=N&Skip=0&Take=2000`;
      let callapi = await repository.get<MessageResponse<CategoryTree[]>>(
        urlSearch
      );

      if (isNullOrUndefined(callapi) || isNullOrUndefinedArry(callapi?.data)) {
        setDataCategory([]);
      } else {
        setDataCategory(callapi?.data ?? []);
      }

      // Load existing categories for edit mode chỉ khi store chưa có dữ liệu
      if (attributeId && store.getSelectedCategories().length === 0) {
        console.log(
          "Loading categories from API for attributeId:",
          attributeId
        );
        await loadAttributeCategories();
      }
    } catch (error: any) {
      NotificationExtension.Fails("Có lỗi xảy ra khi tải dữ liệu !");
    } finally {
      close();
    }
  };

  const loadAttributeCategories = async () => {
    try {
      let urlGetAttribute = API_ROUTE.MODIFY_ATTRIBUTE + `?id=${attributeId}`;
      let response = await repository.get<any>(urlGetAttribute);

      console.log("API Response for attribute categories:", response);

      if (
        response &&
        response.data &&
        response.data.listTblCategoryAttributeModels
      ) {
        console.log(
          "Found categories in API response:",
          response.data.listTblCategoryAttributeModels
        );
        setSelectedCategories(response.data.listTblCategoryAttributeModels);
        store.setSelectedCategories(
          response.data.listTblCategoryAttributeModels
        );
      } else if (
        response &&
        response.data &&
        response.data.listTblCategoryModels
      ) {
        console.log(
          "Found listTblCategoryModels in API response:",
          response.data.listTblCategoryModels
        );
        // Map từ TblCategoryModel sang TblCategoryAttributeModel
        const mappedCategories = response.data.listTblCategoryModels.map(
          (cat: any) => ({
            id: 0,
            attributeId: attributeId || 0,
            categoryId: cat.id,
            orderNumber: 0,
            count: 0,
            value: cat.categoryName || "",
            status: "Y",
            codeAttr: cat.categoryCode,
            nameAttr: cat.categoryName,
            creationDate: new Date().toISOString(),
            createdBy: "",
            lastUpdateDate: new Date().toISOString(),
            lastUpdatedBy: "",
            lastUpdatedLogin: "",
          })
        );
        setSelectedCategories(mappedCategories);
        store.setSelectedCategories(mappedCategories);
      } else {
        console.log(
          "No categories found in API response, full response:",
          response
        );
      }
    } catch (error) {
      console.log("Error loading attribute categories:", error);
    }
  };

  const handleClickCategoryName = (id: number) => {
    var element = document.getElementById(id.toString());
    if (element) {
      element.style.display =
        element.style.display === "block" ? "none" : "block";
    }
  };

  const handleChangeCheckBox = (category: CategoryTree, checked: boolean) => {
    const categoryId = category?.id;

    if (!checked) {
      setSelectedCategories((prev) =>
        prev.filter((selected) => selected.categoryId !== categoryId)
      );
    } else {
      const newCategory: TblCategoryAttributeModel = {
        id: 0,
        attributeId: attributeId || 0,
        categoryId: categoryId,
        orderNumber: 0,
        count: 0,
        value: category?.categoryName || "",
        status: "Y",
        codeAttr: category?.categoryCode,
        nameAttr: category?.categoryName,
        creationDate: new Date().toISOString(),
        createdBy: "",
        lastUpdateDate: new Date().toISOString(),
        lastUpdatedBy: "",
        lastUpdatedLogin: "",
      };
      setSelectedCategories((prev) => [...prev, newCategory]);
    }
  };

  const flattenCategoryTree = (
    categories: CategoryTree[],
    level = 0,
    idParent = 0
  ) => {
    return (
      <div
        id={idParent.toString()}
        style={{ display: level && !showAllCategories ? "none" : "block" }}
      >
        {categories?.flatMap((category) => {
          return (
            <React.Fragment key={category?.id}>
              <Flex pl={level * 30} mt={5}>
                <Text mr={5} mt={-3}>
                  +
                </Text>
                <Checkbox
                  label={
                    category?.categoryChildModels.length > 0 ? (
                      <Text
                        c="blue"
                        td="underline"
                        style={{ cursor: "pointer" }}
                        onClick={() => handleClickCategoryName(category.id)}
                      >
                        {category?.categoryName}
                      </Text>
                    ) : (
                      category?.categoryName
                    )
                  }
                  value={category?.id}
                  checked={
                    !!selectedCategories.find(
                      (selected) => selected?.categoryId === category?.id
                    )
                  }
                  onChange={(e) =>
                    handleChangeCheckBox(category, e.currentTarget.checked)
                  }
                />
              </Flex>

              {category?.categoryChildModels.length > 0 &&
                flattenCategoryTree(
                  category.categoryChildModels,
                  level + 1,
                  category.id
                )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  return (
    <Box
      className="flex-none"
      component="form"
      miw={1300}
      maw={1400}
      mx="auto"
      style={{ position: "relative", minHeight: 500 }}
    >
      <LoadingOverlay
        visible={visible}
        zIndex={1000}
        overlayProps={{ radius: "sm", blur: 2 }}
      />

      <Text mt={"xs"} fw={600}>
        Danh mục được chọn cho thuộc tính:
      </Text>

      <Flex
        gap="md"
        wrap="wrap"
        justify="flex-start"
        align="flex-start"
        style={{
          display: "flex",
          flexWrap: "wrap",
        }}
      >
        {selectedCategories?.map((category, index) => {
          console.log(`Category ${index}:`, category);

          // Tìm tên danh mục từ cây danh mục
          const findCategoryName = (
            categoryId: number,
            categories: CategoryTree[]
          ): string => {
            for (const cat of categories) {
              if (cat.id === categoryId) {
                return cat.categoryName || "";
              }
              if (cat.categoryChildModels?.length > 0) {
                const found = findCategoryName(
                  categoryId,
                  cat.categoryChildModels
                );
                if (found) return found;
              }
            }
            return "";
          };

          const displayName =
            (category.categoryId
              ? findCategoryName(category.categoryId, dataCategory)
              : "") ||
            category?.nameAttr ||
            category?.value ||
            "Không có tên";

          return (
            <Box
              key={index}
              style={{
                marginBottom: "5px",
              }}
            >
              <Text
                style={{
                  whiteSpace: "pre",
                }}
              >
                <strong
                  style={{
                    color: "#3598dc",
                    fontWeight: 500,
                  }}
                >
                  - {displayName}
                </strong>
              </Text>
            </Box>
          );
        })}
      </Flex>

      <Text mt={"xs"} fw={600}>
        Hãy chọn danh mục cho thuộc tính
      </Text>

      <Button
        color="blue"
        variant="outline"
        onClick={() => setShowAllCategories(!showAllCategories)}
        mb="md"
      >
        {showAllCategories
          ? "Ẩn tất cả danh mục con"
          : "Hiển thị tất cả danh mục con"}
      </Button>

      <Box>{flattenCategoryTree(dataCategory || [])}</Box>

      <Group
        justify="flex-end"
        mt="lg"
        pt={"10px"}
        style={{ position: "sticky", bottom: 10, backgroundColor: "white" }}
      >
        <Button
          type="button"
          color="green"
          onClick={onSaveFinal}
          leftSection={<IconCheck size={18} />}
        >
          Lưu
        </Button>
        <ButtonClose />
      </Group>
    </Box>
  );
};

export default React.memo(AttributeCategoryManagerNew);
